import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WorkspaceDrawerTrigger } from "../../components/layout/WorkspaceDrawerTrigger.js";
import { cn } from "../../lib/utils.js";
import { useReportsWorkspace } from "./ReportsWorkspaceLayout.js";

const headerShellClass =
  "flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.94)_100%)] px-4 py-3.5";

export function ReportPageHeader({
  title,
  description,
  icon: Icon,
  actions,
  className,
  showMobileNav = true,
}: {
  title: string;
  description?: string;
  icon: LucideIcon;
  actions?: React.ReactNode;
  className?: string;
  showMobileNav?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { openMobileNav, mobileNavOpen } = useReportsWorkspace();

  return (
    <header
      className={cn(headerShellClass, className)}
      data-testid="reports-page-header"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="inline-grid size-9 shrink-0 place-items-center rounded-xl border border-line/70 bg-surface text-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
          <Icon size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h1 className="m-0 truncate text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
            {title}
          </h1>
          {description ? (
            <p className="m-0 text-[0.75rem] text-muted">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {showMobileNav ? (
          <WorkspaceDrawerTrigger
            label={t("reports.nav.mobileMenu")}
            open={mobileNavOpen}
            onClick={openMobileNav}
            testId="reports-open-mobile-nav"
            ariaLabel={t("reports.nav.mobileMenu")}
          />
        ) : null}
        {actions}
      </div>
    </header>
  );
}
